import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  CardButton,
} from '@duck/ui'
import { InformationCircleIcon, UserPlusIcon, ArrowRightEndOnRectangleIcon } from '@heroicons/react/24/outline'
import { SignupForm } from './SignupForm'
import { GoogleSignInButton } from '../LoginDialog/GoogleSignInButton'
import { LinkedInSignInButton } from '../LoginDialog/LinkedInSignInButton'
import { useTar } from '@/modules'
import { GoogleLoadingView, LinkedInLoadingView } from '../AuthComponents/AuthSocialLoadingViews'
import { StandardLoader } from '../StandardLoader'

type UserTypeSelection = 'new' | 'existing' | null

export const SignupDialog = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const [isStep2, setIsStep2] = useState(false)
  const [showGoogleExpanded, setShowGoogleExpanded] = useState(false)
  const [showLinkedInExpanded, setShowLinkedInExpanded] = useState(false)
  const [showEmailWarning, setShowEmailWarning] = useState(false)
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [userTypeSelection, setUserTypeSelection] = useState<UserTypeSelection>(null)
  const {
    isSignupDialogOpen,
    invitationLoading,
    closeSignupDialog,
    openLoginDialog,
    isRegistering,
    signInWithGoogle,
    signInWithLinkedIn,
    error,
    invitationData
  } = useAuth()
  const { t } = useTar()

  const handleStepChange = useCallback((step: 1 | 2) => {
    setIsStep2(step === 2)
  }, [])

  // Reset state when dialog closes
  useEffect(() => {
    if (!isSignupDialogOpen) {
      setIsStep2(false)
      setShowGoogleExpanded(false)
      setShowLinkedInExpanded(false)
      setShowEmailWarning(false)
      setShowEmailForm(false)
      setUserTypeSelection(null)
    }
  }, [isSignupDialogOpen])

  const handleNewUserClick = () => {
    setUserTypeSelection('new')
  }

  const handleExistingUserClick = () => {
    openLoginDialog()
  }

  const handleGoogleSignUp = () => {
    if (isGoogleLoading) return

    // If no invitation, proceed directly
    if (!invitationData?.email) {
      handleGoogleContinue()
      return
    }

    // First click: show expanded view, close LinkedIn if open
    if (!showGoogleExpanded) {
      setShowLinkedInExpanded(false)
      setShowGoogleExpanded(true)
      return
    }
  }

  const handleGoogleContinue = async () => {
    if (isGoogleLoading) return
    try {
      setIsGoogleLoading(true)
      if (signInWithGoogle) {
        await signInWithGoogle()
      }
    } catch (error) {
      console.error('Google sign-up error:', error)
      setIsGoogleLoading(false)
    }
  }

  const handleLinkedInSignUp = () => {
    if (isLinkedInLoading) return

    // If no invitation, proceed directly
    if (!invitationData?.email) {
      handleLinkedInContinue()
      return
    }

    // First click: show expanded view, close Google if open
    if (!showLinkedInExpanded) {
      setShowGoogleExpanded(false)
      setShowLinkedInExpanded(true)
      return
    }
  }

  const handleLinkedInContinue = async () => {
    if (isLinkedInLoading) return
    try {
      setIsLinkedInLoading(true)
      if (signInWithLinkedIn) {
        await signInWithLinkedIn()
      }
    } catch (error) {
      console.error('LinkedIn sign-up error:', error)
      setIsLinkedInLoading(false)
    }
  }

  const handleEmailLinkClick = () => {
    if (showEmailWarning) {
      // Second click: show the email form
      setShowEmailWarning(false)
      setShowEmailForm(true)
    } else {
      // First click: show the warning tooltip
      setShowEmailWarning(true)
    }
  }

  const isAnyLoading = isGoogleLoading || isLinkedInLoading || isRegistering || invitationLoading

  const invitationLabel = useMemo(() => {
    if (invitationData?.isPublicAssignment) {
      return t('common.signupWithPublicAssignment', { organizationName: invitationData.organizationName, taskName: invitationData.taskName })
    }
    if (invitationData?.inviterName || invitationData?.organizationName) {
      return t('common.signupWithInvitationFromX', { inviter: invitationData.inviterName || invitationData.organizationName })
    }
    return t('common.signupWithInvitation')
  }, [invitationData, t])

  return (
    <Dialog
      open={isSignupDialogOpen}
      onOpenChange={() => { /* Signup dialog is unclosable via overlay/escape */ }}
    >
      <DialogContent
        className="sm:max-w-md rounded-none sm:rounded-lg sm:min-w-[480px]"
        overlayClassName="bg-black"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {t('auth.welcomeToAppName')}
          </DialogTitle>
        </DialogHeader>

        {invitationLoading ? (
          <div className="flex items-center justify-center p-4 bg-accent/10 rounded-lg">
            <StandardLoader size="sm" />
            <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
          </div>
        ) : isLinkedInLoading ? (
          <LinkedInLoadingView />
        ) : isGoogleLoading ? (
          <GoogleLoadingView />
        ) : (
          <div className="space-y-6">
            {/* Invitation info - always show when present and in step 1 */}
            {!isStep2 && invitationData && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                <div className="flex items-center">
                  <InformationCircleIcon className="w-5 h-5 text-success me-2 flex-shrink-0" />
                  <div>
                    <p className="text-md text-primary">
                      {invitationLabel}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* User type selection cards - show only when no selection made */}
            {!isStep2 && userTypeSelection === null && (
              <div className="grid grid-cols-2 gap-4">
                <CardButton
                  icon={<UserPlusIcon className="w-6 h-6 text-accent" />}
                  title={t('auth.newUser')}
                  description={t('auth.createAccountForChallenge')}
                  onClick={handleNewUserClick}
                  variant="default"
                />
                <CardButton
                  icon={<ArrowRightEndOnRectangleIcon className="w-6 h-6 text-accent" />}
                  title={t('auth.existingUser')}
                  description={t('auth.addChallengeToExistingAccount')}
                  onClick={handleExistingUserClick}
                  variant="default"
                />
              </div>
            )}

            {/* Registration options - show only after selecting "New User" */}
            {!isStep2 && userTypeSelection === 'new' && !showEmailForm && (
              <div className="flex flex-col space-y-3">
                {/* LinkedIn Sign-Up Button */}
                <div className="flex flex-col">
                  <LinkedInSignInButton
                    onClick={handleLinkedInSignUp}
                    isDisabled={isAnyLoading}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${showLinkedInExpanded && invitationData?.email
                      ? 'max-h-96 opacity-100 mt-2'
                      : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="bg-card border border-border rounded-lg p-4 flex flex-col">
                      <p className="text-sm text-foreground mb-3">
                        {t('auth.registrationViaLinkedInMustBeWithAccountLinkedToEmail')}{' '}
                        <span className="font-mono font-semibold text-accent">{invitationData?.email}</span>
                      </p>
                      <button
                        onClick={handleLinkedInContinue}
                        disabled={isAnyLoading}
                        className="text-sm text-accent hover:text-foreground transition-colors font-medium self-center"
                      >
                        {t('common.continue')}
                      </button>
                    </div>
                  </div>
                </div>
                {/* Google Sign-Up Button */}
                <div className="flex flex-col">
                  <GoogleSignInButton
                    onClick={handleGoogleSignUp}
                    isDisabled={isAnyLoading}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${showGoogleExpanded && invitationData?.email
                      ? 'max-h-96 opacity-100 mt-2'
                      : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="bg-card border border-border rounded-lg p-4 flex flex-col">
                      <p className="text-sm text-foreground mb-3">
                        {t('auth.registrationViaGoogleMustBeWithAccountLinkedToEmail')}{' '}
                        <span className="font-mono font-semibold text-accent">{invitationData?.email}</span>
                      </p>
                      <button
                        onClick={handleGoogleContinue}
                        disabled={isAnyLoading}
                        className="text-sm text-accent hover:text-foreground transition-colors font-medium self-center"
                      >
                        {t('common.continue')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message - Show in both steps */}
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-error me-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-error">
                      {t('auth.signupError')}
                    </h4>
                    <p className="text-sm text-error mt-1">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Email Sign-Up - only show after selecting "New User" */}
            {userTypeSelection === 'new' && (
              showEmailForm ? (
                <SignupForm onStepChange={handleStepChange} />
              ) : (
                !isStep2 && (
                  <div className="flex justify-center">
                    <TooltipProvider>
                      <Tooltip open={showEmailWarning}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={handleEmailLinkClick}
                            disabled={isAnyLoading}
                            className={`text-sm font-medium transition-colors ${isAnyLoading
                              ? 'text-muted cursor-not-allowed'
                              : 'text-accent hover:text-foreground'
                              }`}
                          >
                            {t('auth.continueWithEmail')}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px] text-center p-3">
                          <p className="text-sm">
                            {t('auth.emailSignupWarning')}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
} 